import React from "react";
import {
  Box,
  List,
  Typography,
  ListItemButton,
} from "@mui/material";
import { Link as RouterLink, useLocation, Navigate } from "react-router-dom";

import overviewIcon from "../assets/menu-icons/figma/overview.svg";
import activitiesIcon from "../assets/menu-icons/figma/activities.svg";
import leadsIcon from "../assets/menu-icons/figma/leads.svg";
import companiesIcon from "../assets/menu-icons/figma/companies.svg";
import jobsIcon from "../assets/menu-icons/figma/jobs.svg";
import candidatesIcon from "../assets/menu-icons/figma/candidates.svg";
import contentIcon from "../assets/menu-icons/figma/content.svg";
import messagesIcon from "../assets/menu-icons/figma/messages.svg";
import teamIcon from "../assets/menu-icons/figma/team.svg";
import insightsIcon from "../assets/menu-icons/figma/insights.svg";
import settingsIcon from "../assets/menu-icons/figma/settings.svg";
import sidebarLogo from "../assets/sidebar-figma-logo.svg";
import { createSidebarResetState } from "../utils/sidebarNavigation";

const iconAssets = {
  overview: overviewIcon,
  activities: activitiesIcon,
  leads: leadsIcon,
  companies: companiesIcon,
  jobs: jobsIcon,
  candidates: candidatesIcon,
  content: contentIcon,
  messages: messagesIcon,
  team: teamIcon,
  insights: insightsIcon,
  settings: settingsIcon,
};

export default function SidebarNav({ user, onLogout, closeDrawer }) {
  const location = useLocation();

  // Redirect if user is undefined
  if (!user) {
    return <Navigate to="/" replace />;
  }

  const links = [
    { name: "Overview", path: "/overview", icon: "overview" },
    { name: "Activities", path: "/activities", icon: "activities" },
    { name: "Leads", path: "/leads", icon: "leads" },
    { name: "Companies", path: "/companies", icon: "companies" },
    { name: "Jobs", path: "/jobs", icon: "jobs" },
    { name: "Candidates", path: "/candidates", icon: "candidates" },
    { name: "Content", path: "/content", icon: "content" },
    { name: "Conversations", path: "/messages-sequences", icon: "messages" },
    { name: "Team", path: "/team", icon: "team" },
    { name: "Insights", path: "/insights-reporting", icon: "insights" },
    { name: "Settings", path: "/settings", icon: "settings" },
  ];

  const filteredLinks = links.filter((link) => {
    if (link.path === "/settings") return ["admin", "administrator", "recruiter", "system_user"].includes(String(user.role || "").trim().toLowerCase());
    if (user.role === "administrator") return true;

    if (user.role === "candidate") {
      return ![
        "Team",
        "Settings",
        "Leads",
        "Insights / Reporting",
        "Tasks",
        "Candidates",
        "Companies",
      ].includes(link.name);
    }
    return true;
  });

  const handleLinkClick = () => closeDrawer?.();

  return (
    <Box className="sidebar-nav" sx={{ textAlign: "center" }}>
      <List className="sidebar-menu" disablePadding>
        {filteredLinks.map((link) => {
          const isActive = location.pathname === link.path;
          const iconAsset = iconAssets[link.icon];

          return (
            <ListItemButton
              key={link.path}
              component={RouterLink}
              to={{ pathname: link.path, search: "" }}
              replace={location.pathname === link.path && !location.search && !location.hash}
              state={createSidebarResetState(link.path)}
              onClick={handleLinkClick}
              className="sidebar-menu-item"
              sx={{
                // ✅ Responsive layout:
                // - xs: grid tiles
                // - sm+: vertical menu
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 0.4, sm: 0.3 },
                color: isActive ? "#fff" : "rgba(255,255,255,0.75)",
                transition: "all 0.2s ease",
                borderRadius: 1,

                // spacing per item
                mx: { xs: 0, sm: 1 },
                my: { xs: 0, sm: 0.6 },
                px: { xs: 0.5, sm: 1 },
                py: { xs: 1.1, sm: 1.2 },

                // active state
                ...(isActive && {
                  color: "#fff",
                }),

                "&:hover": {
                  color: "#fff",
                },
              }}
            >
              <Box className={`sidebar-icon-frame${isActive ? " active" : ""}`} aria-hidden="true">
                <Box
                  className="sidebar-figma-icon"
                  sx={{ maskImage: `url(${iconAsset})`, WebkitMaskImage: `url(${iconAsset})` }}
                />
              </Box>

              <Typography
                sx={{
                  fontSize: "10px",
                  fontWeight: 400,
                  lineHeight: "20px",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  color: "inherit",
                }}
              >
                {link.name}
              </Typography>
            </ListItemButton>
          );
        })}
      </List>

      <div className="sidebar-logo-container">
        <p>Powered by:</p>
        <img
          className="sidebar-logo"
          src={sidebarLogo}
          alt="RPO logo"
        />
      </div>
    </Box>
  );
}
